// Wraps a group of `popover`-controllered elements in a tippy.js singleton.
// Result: hovering between siblings transitions instantly (no show/hide delay
// dance) — like a top-nav menu — while the first hover and final mouseleave
// still respect the per-element show/hide delays.
//
// Also handles a special case for groups whose children are Rails UJS remote
// triggers (e.g. reaction pills): when one is clicked, the popover stays open
// across the AJAX → morph round-trip and its remote content refreshes in
// place. Clicking a pill whose popover wasn't already open does nothing
// special — the popover stays closed.
//
// Usage:
//   <div data-controller="popover-group" data-popover-group-selector-value=".js-foo">
//     <a class="js-foo" data-controller="popover" ...>...</a>
//     <a class="js-foo" data-controller="popover" ...>...</a>
//   </div>

import { Controller } from 'stimulus'
import { createSingleton } from 'tippy.js'

export default class extends Controller {
  static values = {
    selector: { type: String, default: '[data-controller~="popover"]' },
  }

  connect() {
    // Children's tippy instances are created via setTimeout(0) inside their
    // own popover_controller; wait for those to land before grouping.
    this._timeout = setTimeout(() => this._buildSingleton(), 30)
    this.element.addEventListener('ajax:success', this._onAjaxSuccess)
    this.element.addEventListener(
      'ajax-update:complete',
      this._onAjaxUpdateComplete
    )
  }

  disconnect() {
    clearTimeout(this._timeout)
    this.element.removeEventListener('ajax:success', this._onAjaxSuccess)
    this.element.removeEventListener(
      'ajax-update:complete',
      this._onAjaxUpdateComplete
    )
    this._singleton?.destroy()
  }

  // Fires (delegated) on a child pill's Rails UJS success. If the popover is
  // currently showing, kick off a fresh fetch of this child's remote content
  // so we have updated reactor data ready to swap in once the morph settles.
  _onAjaxSuccess = (event) => {
    const child = event.target.closest(this.selectorValue)
    const url = child?.dataset.contentUrl
    if (!url || !this._popoverIsOpen()) return

    this._pendingChild = child
    this._pendingContent = fetch(url, { cache: 'no-store' }).then((r) =>
      r.text()
    )
  }

  // Fires after `AjaxHelper.doUpdate` finishes the morph_replace. Apply the
  // freshly-fetched content to the child's tippy — its `content` prop is in
  // tippy's createSingleton overrides list, so the singleton's shared popper
  // picks up the update automatically when the child is the active target.
  _onAjaxUpdateComplete = async () => {
    if (!this._pendingChild || !this._pendingContent) return
    const child = this._pendingChild
    const promise = this._pendingContent
    this._pendingChild = null
    this._pendingContent = null

    const html = await promise
    child._tippy?.setContent(html)
  }

  _popoverIsOpen() {
    return !!document.querySelector(
      '.popover-controller-tippy .tippy-box[data-state="visible"]'
    )
  }

  _buildSingleton() {
    const tippies = Array.from(
      this.element.querySelectorAll(this.selectorValue)
    )
      .map((el) => el._tippy)
      .filter(Boolean)
    if (tippies.length < 2) return

    const refDataset = tippies[0].reference.dataset

    this._singleton = createSingleton(tippies, {
      allowHTML: true,
      // `hideOnClick: false` so tippy's documentMouseDown handler doesn't
      // auto-hide when a child reference is clicked. Combined with leaving
      // `click` out of the children's trigger, this gives us:
      //   click while open  → popover stays open (and we refresh its content
      //                       via the ajax handlers above)
      //   click while closed → popover stays closed (no `click` trigger, no
      //                        hideOnClick logic)
      // Mouseleave still hides the popover the way you'd expect.
      hideOnClick: false,
      // The singleton creates its own popper and does NOT inherit `interactive`
      // from the child tippies. Without these, moving the cursor from the pill
      // into the popover crosses the offset gap and triggers hide.
      interactive: tippies[0].props.interactive ?? true,
      interactiveBorder: tippies[0].props.interactiveBorder ?? 16,
      delay: [
        tippies[0].props.delay?.[0] ?? 250,
        tippies[0].props.delay?.[1] ?? 200,
      ],
      moveTransition: 'transform 0.15s ease-out',
      overrides: ['placement', 'theme'],
      onCreate(instance) {
        // Mirror the popper decoration that popover_controller.js does in its
        // own onCreate (the singleton creates a fresh popper that misses it),
        // plus add `.tw` so descendants get the Tailwind utility scope —
        // Popper lives at document.body so we have to bring `.tw` to it.
        instance.popper.classList.add('tw', 'popover-controller-tippy')
        if (refDataset.tooltipClass)
          instance.popper.classList.add(...refDataset.tooltipClass.split(' '))
        if (refDataset.tooltipBoxClass)
          instance.popper
            .querySelector('.tippy-box')
            ?.classList.add(...refDataset.tooltipBoxClass.split(' '))
      },
    })
  }
}
